
import _ from 'lodash';
import fetch, { fetchWithoutDefaultAuthHeaders } from '../utils/fetch-wrapper';
import getClientConfig from './getClientConfig';

import { APIHelper } from './helpers';
import APIEndpoints from './apiEndpoints';
import { HTTPError } from "./errors";
import Logger, { LOG_LEVELS, ALL_MESSAGES } from './logger';

const log = new Logger('CUP/AI_CLIENT', LOG_LEVELS.DEBUG);

// session endpoints are not forwarded by the BFF to the SVR API as the
// BFF handles all session management for AI and legacy auth when it is used.
const sessionManagementUrl = () => {
  const { vrRestApiHost } = getClientConfig();
  return `${vrRestApiHost}/bffapi/v1`;
};

const vrRestApiUrl = (inputVersion) => {
  const { enableAvalaraIdentity, enableAvalaraIdentityJwt, vrRestApiHost } = getClientConfig();

  let version = inputVersion ? inputVersion : 1;

  return (enableAvalaraIdentity || enableAvalaraIdentityJwt)
    ? `${vrRestApiHost}/api/v${version}`
    : `${vrRestApiHost}/api`;
};

const bffApiUrl = (inputVersion) => {
  const { vrRestApiHost } = getClientConfig();

  let version = inputVersion ? inputVersion : 1;

  return `${vrRestApiHost}/bffapi/v${version}`;
}

const formatUserToCreate = (user) => {
  const { enableAvalaraIdentity, enableAvalaraIdentityJwt } = getClientConfig();

  if (!(enableAvalaraIdentity || enableAvalaraIdentityJwt)) return user;

  const v1User = {
    ...user,
    emailAddress: user.id,
    userName: user.id
  };
  delete v1User.id;
  return v1User;
};

const PASSWORD_ERRORS = new Map([
  ['length', ALL_MESSAGES.MIN_LENGTH_PASSWORD],
  ['weak', ALL_MESSAGES.WEAK_NEW_PASSWORD],
  ['reserved', ALL_MESSAGES.RESERVED_PASSWORD],
  ['sameasusername', ALL_MESSAGES.SAME_AS_USERNAME_PASSWORD]
]);

function errorHandler(response) {
  if (!response.ok) {
    if (response.status === 500) {
      log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
    }
    //On session expired/logged out, redirect use to login page
    if (response.status === 401) {
      //redirect to login page, this.props.history.push is not working since prop is undefined
      window.open("/", "_self");
    }
    throw new HTTPError(response.status, response.statusText);
  }
  return response;
}

export default {
  getUsers(apiVersion = 1) {
    log.debug('getUsers ');
    return new Promise((resolve, reject) => {
      const { vrRestApiHost } = getClientConfig();

      // V2 should be handled by BFF
      // V1 should be passed by BFF to SVR REST API
      const urlSegment = (apiVersion == 2) ? "bffapi" : "api";
      const uri = `${vrRestApiHost}/${urlSegment}/v${apiVersion}/users`;

      return fetch(uri, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
      })
        .then(errorHandler)
        .then((response) => {
          return response.json();
        })
        .then((json) => {
          log.debug('getUsers: success: ', json);
          return resolve(json);
        })
        .catch((error) => {
          log.debug('getUsers: error:', error);
          reject(error);
        });
    });
  },
  updateUser(id, user, apiVersion = 1) {
    log.debug('updateUser() ', id, user);
    const { vrRestApiHost } = getClientConfig();
    const uri = `${vrRestApiHost}/bffapi/v${apiVersion}/users/${encodeURIComponent(id)}`;
    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'PUT',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user),
      })

        .then((response) => {
          log.debug('updateUser: response:', response);
          if (response.status == 401)
            reject({ status: response.status, statusText: 'Unauthorized' });

          if (response.status == 200 || response.status == 204)
            return resolve(response);

          return reject({ status: response.status, statusText: 'UnknownError' });
        })
        .catch((error) => {
          log.debug('updateUser: error:', error);
          reject(error);
        });
    });
  },
  createUser(user) {
    user = formatUserToCreate(user);
    log.debug('createUser() ', user);
    const { vrRestApiHost } = getClientConfig();
    const uri = `${vrRestApiHost}/bffapi/v1/users`;
    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user),
      })
        .then(errorHandler)
        .then((response) => {
          return response.json();
        })
        .then((json) => {
          log.debug('createUser: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('createUser: error:', error);
          reject(error);
        });
    });
  },
  resetUserPassword(resetToken, newPassword) {
    // Note : Michael Marshall | 2024-06 I don't think this is used. User password is managed by Avalara Identity.
    const { vrRestApiHost } = getClientConfig();

    log.debug('resetUserPassword() ');
    const uri = `${vrRestApiUrl()}/password/` + encodeURIComponent(resetToken);
    return new Promise((resolve, reject) => {
      return fetchWithoutDefaultAuthHeaders(uri, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 'Id': '', 'Password': btoa(newPassword) }),
      })
        .then((response) => {
          if (response.status === 400) {
            response.json().then((errorData) => {
              if (response.status === 400) {
                const status = errorData.status || [];
                let errorNotified = false;
                status.forEach((errorStatus) => {
                  PASSWORD_ERRORS.forEach((value, key) => {
                    if (errorStatus.statusCode.includes(key)) {
                      log.setDictionaryMessage(value);
                      errorNotified = true;
                    }
                  });
                });
                if (errorNotified === false) {
                  log.setDictionaryMessage(ALL_MESSAGES.RESET_PASSWORD_ERROR);
                }
              }
              reject(errorData);
            });
          } else {
            return response.json();
          }
        })
        .then((json) => {
          log.debug('resetUserPassword: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('resetUserPassword: error:', error);
          reject(error);
        });
    });
  },
  updateUserPassword(id, oldPassword, newPassword) {
    // Note : Michael Marshall | 2024-06 I don't think this is used. User password is managed by Avalara Identity.
    log.debug('UpdatePassword() ', id, oldPassword, newPassword);
    const uri = vrRestApiUrl() + '/Users/' + encodeURIComponent(id) + '/changePassword';
    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 'OldPassword': btoa(oldPassword), 'NewPassword': btoa(newPassword) }),
      })
        .then((response) => {
          if (response.status === 401) {
            log.setDictionaryMessage(ALL_MESSAGES.INCORRECT_OLD_PASSWORD);
          } else if (response.status === 400) {
            response.json().then((errorData) => {
              if (response.status === 400) {
                const status = errorData.status || [];
                status.forEach((errorStatus) => {
                  PASSWORD_ERRORS.forEach((value, key) => {
                    if (errorStatus.statusCode.includes(key)) {
                      log.setDictionaryMessage(value);
                    }
                  });
                });
              }
              reject(errorData);
            });
          } else {
            return response.json();
          }
        })
        .then((json) => {
          log.debug('updateUserPassword: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('updateUserPassword: error:', error);
          reject(error);
        });
    });
  },
  deleteUser(id, apiVersion = 1) {
    log.debug('deleteUser(): ', id);
    const { vrRestApiHost } = getClientConfig();
    const uri = `${vrRestApiHost}/bffapi/v${apiVersion}/users/${encodeURIComponent(id)}`;
    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'DELETE',
        mode: 'cors',
        cache: 'no-cache'
      })
        .then((response) => {
          if (response.status == 401)
            return reject({ status: response.status, statusText: 'Unauthorized' });

          if (response.status == 204)
            return resolve(response);

          return reject({ status: response.status, statusText: 'UnknownError' });
        })
        .catch((error) => {
          log.debug('deleteUser: error:', error);
          reject(error);
        });
    });
  },
  login(username, password) {
    // Authentication based on: https://developer.avalara.com/avatax/authentication-in-rest/
    var authHeader = 'Basic ' + btoa(username + ':' + password);
    var status;
    log.debug('login: authenticating %s / %s : ', username, password, authHeader);
    return new Promise((resolve, reject) => {
      return fetch(`${sessionManagementUrl()}/sessions`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Authorization: authHeader
        }
      })
        .then((response) => {
          log.debug('login: got response: ', response);
          status = response.status;
          if (status === 200) {
            return response.json();
          }
          // TODO: Check some more status codes
          throw new ALL_MESSAGES(ALL_MESSAGES.INVALID_LOGIN_CREDENTIALS);
        })
        .then((json) => {
          log.debug('login: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('login: error: ', status, error);

          reject({ status: status });
          // TODO: We might want to do something clever with a logout one day
          // this.logout()
          //   .then((response) => {
          //     reject({message: 'Unknown username / password'})
          //   })
          //   .catch((error) => {
          //     reject({message: 'Unknown username / password'})
          //   });
        });
    });
  },
  authenticateToken() {
    const { enableAvalaraIdentity, enableAvalaraIdentityJwt } = getClientConfig();
    const token = sessionStorage.token;
    log.debug('authenticateToken: authenticating %s ', token);

    const headers = {};

    if (!enableAvalaraIdentity || enableAvalaraIdentityJwt) {
      headers.Authorization = token
    }

    return new Promise((resolve, reject) => {
      return fetch(`${sessionManagementUrl()}/sessions`, {
        method: 'POST',
        mode: 'cors'
      })
        .then((response) => {
          if (response.status === 200) {
            return response.json();
          }
          throw new Error('Failed to authenticate', response);
        })
        .then((json) => {
          log.debug('authenticateToken: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('authenticateToken: error', error);
          reject({ message: 'Invalid token' });

          // TODO: We might want to do something clever with a logout.
          // this.logout()
          //   .then((response) => {
          //     reject({message: 'Invalid token'})
          //   })
          //   .catch((error) => {
          //     reject({message: 'Invalid token'})
          //   });
        });
    });
  },
  loggedIn() {
    // setTimeout(function() {
    //   callback(!!sessionStorage.token);
    // }, 500);
    return !!sessionStorage.token;
  },
  isAuthorised(location) {
    log.debug('isAuthorised: ', location);
    return this.loggedIn();
  },
  getProducts() {
    log.debug('getProducts():');
    return new Promise((resolve, reject) => {
      const requestUrl = vrRestApiUrl() + '/Content/Products';
      const init = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
      };
      return fetch(requestUrl, init).then((response) => {
        log.debug('getProducts(): response:', response);
        if (response.status === 200) {
          resolve(response.json());
        }
      }).catch((error) => {
        log.debug('getProducts(): error: ', error);
        reject({ message: 'Error in fetching subscriptions' });
      });
    });
  },
  getCustomers() {
    log.debug('getCustomers():');
    return new Promise((resolve, reject) => {
      const requestUrl = vrRestApiUrl() + '/Content/Customers';
      const init = {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
      };
      return fetch(requestUrl, init).then((response) => {
        log.debug('getCustomers(): response:', response);
        if (response.status === 200) {
          resolve(response.json());
        }
      }).catch((error) => {
        log.debug('getCustomers(): error: ', error);
        reject({ message: 'Error in fetching Customers' });
      });
    });
  },
  masquerade(accountId, licenseKey) {
    // Michael Marshall | 2024-07-01 | Pretty sure this is no longer in use.
    // Masquerade used to be used by support to logon to a customers tenant on behalf of a customer.
    // My understanding is that this is no longer used.
    // TODO : Confirm and delete this nonsense.
    var authHeaderAccount = 'Basic ' + btoa(accountId + ':' + licenseKey);
    log.debug('masquerade: authenticating %s / %s : ', accountId, licenseKey, authHeaderAccount);
    const requestUrl = `${bffApiUrl()}/sessions/masquerade`;
    var status;
    log.debug('masquerade: authenticating Token', sessionStorage.token, requestUrl);
    return new Promise((resolve, reject) => {
      return fetch(requestUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Authorization: authHeaderAccount,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 'SessionToken': sessionStorage.token }),
      })
        .then((response) => {
          log.debug('login: got response: ', response);
          status = response.status;
          if (response.status === 200) {
            return response.json();
          }
          // TODO: Check some more status codes
          throw new ALL_MESSAGES(ALL_MESSAGES.INVALID_LOGIN_CREDENTIALS);
        })
        .then((json) => {
          log.debug('login: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('masquerade: error', error);
          reject({ status: status });
        });
    });
  },
  getLicences(customerId) {
    log.debug('getLicences():', customerId);
    return new Promise((resolve, reject) => {
      const requestUrl = vrRestApiUrl() + '/licences/' + encodeURIComponent(customerId);
      return fetch(requestUrl, {
        method: 'GET',
        mode: 'cors',
      })
        .then((response) => {
          log.debug('getLicences: got response: ', response);
          return response.json();
        })
        .then((json) => {
          resolve(json);
        })
        .catch((error) => {
          log.debug('getLicences: got error: ', error);
          reject(error);
        });
    });
  },

  getRoles() {
    log.debug('getRoles():');
    let URL = APIHelper.getURL(APIEndpoints.getRoles);
    log.debug('getRoles(): URL: ', URL);
    return APIHelper.get(URL, sessionStorage.token);
  },

  getRolesByUserId(userId) {
    log.debug('getRolesByUserId(): userId: ', userId);
    if (_.isEmpty(userId)) return {
      userId: userId,
      roles: {}
    };
    let URL = APIHelper.getURL(APIEndpoints.getRolesByUserId, null, { userId: userId });
    log.debug('getRolesByUserId(): URL: ', URL);
    return APIHelper.get(URL, sessionStorage.token);
  },

  bulkAction(values) {
    log.debug('bulkAction() ', values);
    const { featureFlags } = getClientConfig();
    const enableBulkOperationsV5Endpoint = featureFlags?.EnableBulkOperationsV5Endpoint;

    const uri = enableBulkOperationsV5Endpoint
      ? APIHelper.getURL(APIEndpoints.bulkOperationsV5)
      : APIHelper.getURL(APIEndpoints.bulkOperations, null, { tenantId: values.tenantId });
    const formData = new FormData();
    if (values.file) {
      formData.append('file', values.file);
    }
    const operationName = this.getOperationCommandName(values.action, values.inputType);
    formData.append('tenantId', values.tenantId);
    formData.append('companyCode', values.companyCode);
    formData.append('action', operationName);
    formData.append('reporterName', values.reporterName);
    formData.append('year', values.year);
    formData.append('reason', values.reason);
    formData.append('inputType', values.inputType);
    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'POST',
        mode: 'cors',
        body: formData,
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(errorData => {
              const error = new Error(`API Error: ${response.status}`);
              error.status = response.status;
              error.data = errorData;
              throw error;
            });
          }
          return response.json();
        })
        .then((json) => {
          log.debug('bulkAction: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('bulkAction: error:', error);
          reject({
            status: error.status || 500,
            message: error.data?.message || 'An error occurred.',
            entities: error.data?.entities || []
          });
        });
    });
  },

  getOperationCommandName(action, inputType) {
    if (action == undefined || inputType == undefined) {
      return undefined;
    }
    const commandName = `${action}${inputType}s`;
    return commandName;
  },

  getBulkOperationStatus(tenantId, userId, operationTypeIds) {
    log.debug('getBulkOperationStatus() ', tenantId);

    const uriBase = APIHelper.getURL(APIEndpoints.getBulkOperationStatus,
      {
        userId: userId,
        timespan: 'today',
        sortBy: 'timestamp',
        sortOrder: 'desc'
      },
      {
        tenantId: tenantId
      }
    );

    let operationTypeIdsQuery = '';
    if (operationTypeIds?.length > 0) {
      operationTypeIdsQuery = operationTypeIds.map(id => `operationTypeIds=${id}`).join('&');
    }

    const uri = operationTypeIdsQuery ? `${uriBase}&${operationTypeIdsQuery}` : uriBase;

    return new Promise((resolve, reject) => {
      return fetch(uri, {
        method: 'GET',
        mode: 'cors',
      })
        .then(errorHandler)
        .then((response) => {
          return response.json();
        })
        .then((json) => {
          log.debug('getBulkOperationStatus: success: ', json);
          resolve(json);
        })
        .catch((error) => {
          log.debug('getBulkOperationStatus: error:', error);
          reject(error);
        });
    });
  }
}